import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Trophy, Plus, X, Target, DollarSign, Users, Calendar, Award,
  ChevronRight, Clock, CheckCircle, XCircle, TrendingUp, Gift, Edit2, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '../lib/auth';

const SalesCompetitions = () => {
  const user = authService.getCurrentUser();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [myKitty, setMyKitty] = useState(null);
  const [callerGroups, setCallerGroups] = useState([]);
  const [callerPoints, setCallerPoints] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    caller_group_id: '',
    competition_type: 'sales_count',  // revenue, sales_count, dual
    target_value: '',  // Revenue target
    sales_target: '',  // Sales count target
    winner_type: 'staged',  // first_to_target, highest_at_end, all_achievers, staged
    frequency: 'one_time', // one_time, daily, weekly
    prize_points_1st: '',
    prize_points_2nd: '',
    prize_points_3rd: '',
    prize_points_all_achievers: '',
    stages: [{ sales_target: '', target_value: '', reward_points: '' }],  // Multiple stages
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    target_value: '',
    prize_points_1st: '',
    prize_points_2nd: '',
    prize_points_3rd: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch data separately to prevent one failure from breaking all
      try {
        const competitionsRes = await api.getCompetitions();
        setCompetitions(competitionsRes.data || []);
      } catch (err) {
        console.error('Error fetching competitions:', err);
        setCompetitions([]);
      }
      
      try {
        const kittyRes = await api.getMyKitty();
        setMyKitty(kittyRes.data);
      } catch (err) {
        console.error('Error fetching kitty:', err);
      }
      
      try {
        const groupsRes = await api.getCallerGroups();
        setCallerGroups(groupsRes.data || []);
      } catch (err) {
        console.error('Error fetching caller groups:', err);
        setCallerGroups([]);
      }
      
      // Fetch caller points only for admin/manager
      if (user?.role === 'admin' || user?.role === 'manager') {
        try {
          const pointsRes = await api.getAllCallerPoints();
          setCallerPoints(pointsRes.data || []);
        } catch (err) {
          console.error('Error fetching caller points:', err);
          setCallerPoints([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompetition = async () => {
    // Validate based on winner_type
    const isTargetBased = formData.winner_type === 'all_achievers' || formData.winner_type === 'first_to_target';
    const isStaged = formData.winner_type === 'staged';
    
    // Validation based on competition type
    if (!formData.name || !formData.end_date) {
      toast.error('Please fill name and end date');
      return;
    }
    
    // Staged competitions need valid stages
    if (isStaged) {
      const validStages = formData.stages.filter(s => 
        (s.sales_target || s.target_value) && s.reward_points
      );
      if (validStages.length === 0) {
        toast.error('Please add at least one stage with target and reward');
        return;
      }
    } else {
      if (formData.competition_type === 'dual') {
        if (!formData.target_value && !formData.sales_target) {
          toast.error('Please set at least one target (revenue or sales)');
          return;
        }
      } else if (formData.competition_type === 'revenue') {
        if (!formData.target_value) {
          toast.error('Please set revenue target');
          return;
        }
      } else if (formData.competition_type === 'sales_count') {
        if (!formData.sales_target) {
          toast.error('Please set sales count target');
          return;
        }
      }
    }
    
    // For target-based: need all_achievers points
    if (isTargetBased && !formData.prize_points_all_achievers) {
      toast.error('Please set points for target achievers');
      return;
    }
    
    // For ranking-based: need at least 1st place points
    if (!isTargetBased && !isStaged && !formData.prize_points_1st) {
      toast.error('Please set points for 1st place');
      return;
    }

    try {
      const payload = {
        ...formData,
        target_value: formData.target_value ? parseFloat(formData.target_value) : null,
        sales_target: formData.sales_target ? parseInt(formData.sales_target) : null,
        caller_group_id: formData.caller_group_id || null
      };
      
      // Handle stages for staged competitions
      if (isStaged) {
        payload.stages = formData.stages
          .filter(s => (s.sales_target || s.target_value) && s.reward_points)
          .map(s => ({
            sales_target: s.sales_target ? parseInt(s.sales_target) : null,
            target_value: s.target_value ? parseFloat(s.target_value) : null,
            reward_points: parseInt(s.reward_points)
          }));
        payload.prize_points_1st = null;
        payload.prize_points_2nd = null;
        payload.prize_points_3rd = null;
        payload.prize_points_all_achievers = null;
      } else if (isTargetBased) {
        payload.stages = null;
        payload.prize_points_all_achievers = parseInt(formData.prize_points_all_achievers);
        payload.prize_points_1st = null;
        payload.prize_points_2nd = null;
        payload.prize_points_3rd = null;
      } else {
        payload.stages = null;
        payload.prize_points_1st = parseInt(formData.prize_points_1st);
        payload.prize_points_2nd = formData.prize_points_2nd ? parseInt(formData.prize_points_2nd) : null;
        payload.prize_points_3rd = formData.prize_points_3rd ? parseInt(formData.prize_points_3rd) : null;
        payload.prize_points_all_achievers = null;
      }
      
      await api.createCompetition(payload);
      toast.success('Competition created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating competition:', error);
      toast.error(error.response?.data?.detail || 'Failed to create competition');
    }
  };

  const handleCompleteCompetition = async (competitionId) => {
    if (!window.confirm('Complete this competition and award points to winners?')) return;

    try {
      const res = await api.completeCompetition(competitionId);
      toast.success(`Competition completed! ${res.data.total_points_distributed} points distributed`);
      fetchData();
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error completing competition:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete competition');
    }
  };

  const handleCancelCompetition = async (competitionId) => {
    if (!window.confirm('Cancel this competition? No points will be awarded.')) return;

    try {
      await api.cancelCompetition(competitionId);
      toast.success('Competition cancelled');
      fetchData();
    } catch (error) {
      console.error('Error cancelling competition:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel competition');
    }
  };

  const handleMarkPaidCash = async (winnerId) => {
    if (!window.confirm('Mark this winner\'s incentive as paid in cash? This will be reflected in HR salary.')) return;

    try {
      await api.markWinnerPaidCash(winnerId);
      toast.success('Marked as paid in cash');
      // Refresh competition details
      if (selectedCompetition) {
        const res = await api.getCompetition(selectedCompetition.id);
        setSelectedCompetition(res.data);
      }
      fetchData();
    } catch (error) {
      console.error('Error marking paid in cash:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleEditCompetition = (comp) => {
    setEditFormData({
      name: comp.name || '',
      description: comp.description || '',
      target_value: comp.target_value?.toString() || '',
      prize_points_1st: comp.prize_points_1st?.toString() || '',
      prize_points_2nd: comp.prize_points_2nd?.toString() || '',
      prize_points_3rd: comp.prize_points_3rd?.toString() || '',
      end_date: comp.end_date || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCompetition = async () => {
    if (!selectedCompetition) return;

    try {
      await api.updateCompetition(selectedCompetition.id, {
        name: editFormData.name,
        description: editFormData.description
      });
      toast.success('Competition updated successfully');
      setShowEditModal(false);
      fetchData();
      // Refresh competition details
      const res = await api.getCompetition(selectedCompetition.id);
      setSelectedCompetition(res.data);
    } catch (error) {
      console.error('Error updating competition:', error);
      toast.error(error.response?.data?.detail || 'Failed to update competition');
    }
  };

  const viewCompetitionDetails = async (competition) => {
    try {
      const res = await api.getCompetition(competition.id);
      setSelectedCompetition(res.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching competition details:', error);
      console.error('Competition ID:', competition.id);
      console.error('Response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to load competition details');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      caller_group_id: '',
      competition_type: 'sales_count',
      target_value: '',
      sales_target: '',
      winner_type: 'staged',
      frequency: 'one_time',
      prize_points_1st: '',
      prize_points_2nd: '',
      prize_points_3rd: '',
      prize_points_all_achievers: '',
      stages: [{ sales_target: '', target_value: '', reward_points: '' }],
      start_date: new Date().toISOString().split('T')[0],
      end_date: ''
    });
  };

  const filteredCompetitions = competitions.filter(c => {
    if (activeTab === 'active') return c.status === 'active';
    if (activeTab === 'completed') return c.status === 'completed';
    if (activeTab === 'cancelled') return c.status === 'cancelled';
    if (activeTab === 'expired') return c.status === 'expired';
    return true;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Cancelled</span>;
      case 'expired':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">Expired</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Competitions</h1>
          <p className="text-slate-500">Create and manage incentive competitions for your sales team</p>
        </div>
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="create-competition-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Competition
          </Button>
        )}
      </div>

      {/* Kitty Balance Card (for managers) */}
      {myKitty && user?.role === 'manager' && (
        <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100">Your Point Kitty</p>
                <p className="text-3xl font-bold mt-1">{myKitty.available_balance?.toLocaleString() || 0}</p>
                <p className="text-sm text-amber-100 mt-1">
                  Total Allocated: {myKitty.total_allocated?.toLocaleString() || 0} | 
                  Distributed: {myKitty.total_distributed?.toLocaleString() || 0}
                </p>
              </div>
              <Gift className="w-12 h-12 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Caller Points This Month - For Admin/Manager */}
      {(user?.role === 'admin' || user?.role === 'manager') && callerPoints.length > 0 && (
        <Card data-testid="caller-points-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Star className="w-5 h-5 text-amber-500" />
              Caller Points This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-600">Rank</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-600">Caller</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-slate-600">Points</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-slate-600">Value (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {callerPoints.map((cp, idx) => (
                    <tr key={cp.caller_id} className={`border-t ${idx < 3 ? 'bg-amber-50' : ''}`}>
                      <td className="py-2 px-4">
                        {idx === 0 && <span className="text-xl">🥇</span>}
                        {idx === 1 && <span className="text-xl">🥈</span>}
                        {idx === 2 && <span className="text-xl">🥉</span>}
                        {idx >= 3 && <span className="text-slate-500">#{idx + 1}</span>}
                      </td>
                      <td className="py-2 px-4 font-medium text-slate-900">{cp.caller_name}</td>
                      <td className="py-2 px-4 text-right font-bold text-amber-600">{cp.total_points}</td>
                      <td className="py-2 px-4 text-right text-slate-600">₹{cp.total_points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['active', 'completed', 'cancelled'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium text-sm capitalize transition-colors ${
              activeTab === tab
                ? 'text-indigo-600 border-b-2 border-indigo-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab} ({competitions.filter(c => c.status === tab).length})
          </button>
        ))}
      </div>

      {/* Competitions Grid */}
      {filteredCompetitions.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Trophy className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No {activeTab} Competitions</h3>
              <p className="text-slate-500">
                {activeTab === 'active' ? 'Create a new competition to motivate your team!' : `No ${activeTab} competitions found.`}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredCompetitions.map(comp => (
            <Card 
              key={comp.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => viewCompetitionDetails(comp)}
              data-testid={`competition-card-${comp.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{comp.name}</CardTitle>
                  {getStatusBadge(comp.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {comp.description && (
                    <p className="text-sm text-slate-600 line-clamp-2">{comp.description}</p>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm">
                    <Target className="w-4 h-4 text-indigo-600" />
                    <span className="text-slate-600">
                      {comp.winner_type === 'staged' && comp.stages?.length > 0 ? (
                        // Show stages for staged competitions
                        comp.stages.map((s, i) => (
                          <span key={i}>
                            {i > 0 && ' → '}
                            {s.sales_target || s.target_value}
                          </span>
                        ))
                      ) : comp.competition_type === 'dual' ? (
                        <>
                          {comp.target_value && `₹${comp.target_value.toLocaleString()}`}
                          {comp.target_value && comp.sales_target && ' OR '}
                          {comp.sales_target && `${comp.sales_target} Sales`}
                        </>
                      ) : comp.competition_type === 'revenue' ? (
                        `₹${(comp.target_value || 0).toLocaleString()} Revenue Target`
                      ) : (
                        `${comp.sales_target || comp.target_value || 0} Sales Target`
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Award className="w-4 h-4 text-amber-500" />
                    <span className="text-slate-600">
                      {comp.winner_type === 'staged' && comp.stages?.length > 0 ? (
                        `${comp.stages.length} stages (up to ${Math.max(...comp.stages.map(s => s.reward_points || 0))} pts)`
                      ) : comp.winner_type === 'all_achievers' || comp.winner_type === 'first_to_target' ? (
                        `${comp.prize_points_all_achievers || comp.prize_points_1st} pts per achiever`
                      ) : (
                        <>
                          1st: {comp.prize_points_1st} pts
                          {comp.prize_points_2nd && ` | 2nd: ${comp.prize_points_2nd} pts`}
                          {comp.prize_points_3rd && ` | 3rd: ${comp.prize_points_3rd} pts`}
                        </>
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">
                      {new Date(comp.start_date).toLocaleDateString()} - {new Date(comp.end_date).toLocaleDateString()}
                      {comp.frequency && comp.frequency !== 'one_time' && (
                        <span className="ml-2 text-indigo-600 font-medium">
                          ({comp.frequency === 'daily' ? 'Daily Reset' : 'Weekly Reset'})
                        </span>
                      )}
                    </span>
                  </div>

                  {comp.caller_group_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">{comp.caller_group_name}</span>
                    </div>
                  )}

                  <div className="pt-2 flex justify-end">
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Competition Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Create New Competition</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Competition Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., February Sales Challenge"
                    data-testid="competition-name-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe the competition rules and rewards..."
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={2}
                  />
                </div>

                {/* Caller Group */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Caller Group</label>
                  <select
                    value={formData.caller_group_id}
                    onChange={(e) => setFormData({...formData, caller_group_id: e.target.value})}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Callers</option>
                    {callerGroups.map(group => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                </div>

                {/* Competition Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Competition Type *</label>
                  <select
                    value={formData.competition_type}
                    onChange={(e) => setFormData({...formData, competition_type: e.target.value})}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="dual">Either Revenue OR Sales (Recommended)</option>
                    <option value="revenue">Revenue Based Only</option>
                    <option value="sales_count">Sales Count Based Only</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.competition_type === 'dual' && 'Winner achieves EITHER revenue OR sales target (whichever comes first)'}
                    {formData.competition_type === 'revenue' && 'Winner must achieve revenue target'}
                    {formData.competition_type === 'sales_count' && 'Winner must achieve sales count target'}
                  </p>
                </div>

                {/* Targets based on competition type - Hide for staged competitions */}
                {formData.winner_type !== 'staged' && (
                  <>
                    {formData.competition_type === 'dual' ? (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <label className="block text-sm font-medium text-blue-800 mb-3">
                          Set Target (Achieve EITHER one to win)
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-blue-700 mb-1">Revenue Target (₹)</label>
                            <Input
                              type="number"
                              value={formData.target_value}
                              onChange={(e) => setFormData({...formData, target_value: e.target.value})}
                              placeholder="50000"
                              className="bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-blue-700 mb-1">OR Sales Count Target</label>
                            <Input
                              type="number"
                              value={formData.sales_target}
                              onChange={(e) => setFormData({...formData, sales_target: e.target.value})}
                              placeholder="10"
                              className="bg-white"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-2">
                          Points will be awarded once - when EITHER target is achieved first
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Target {formData.competition_type === 'revenue' ? '(₹)' : '(Count)'} *
                        </label>
                        <Input
                          type="number"
                          value={formData.competition_type === 'revenue' ? formData.target_value : formData.sales_target}
                          onChange={(e) => {
                            if (formData.competition_type === 'revenue') {
                              setFormData({...formData, target_value: e.target.value});
                            } else {
                              setFormData({...formData, sales_target: e.target.value});
                            }
                          }}
                          placeholder={formData.competition_type === 'revenue' ? '50000' : '10'}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Winner Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Winner Selection *</label>
                  <select
                    value={formData.winner_type}
                    onChange={(e) => setFormData({...formData, winner_type: e.target.value})}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="staged">Staged Rewards (Multiple Milestones)</option>
                    <option value="highest_at_end">Highest at Competition End (Ranking-based)</option>
                    <option value="first_to_target">First to Reach Target</option>
                    <option value="all_achievers">All Who Reach Target (Target-based)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.winner_type === 'staged' && 'Callers earn rewards at each milestone they reach'}
                    {formData.winner_type === 'highest_at_end' && 'Points awarded to top performers based on their ranking'}
                    {formData.winner_type === 'first_to_target' && 'Points awarded to first person who reaches the target'}
                    {formData.winner_type === 'all_achievers' && 'Points awarded to everyone who reaches the target'}
                  </p>
                </div>

                {/* Competition Frequency */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="one_time">One Time (Single competition for the period)</option>
                    <option value="daily">Daily (Resets every day)</option>
                    <option value="weekly">Weekly (Resets every week)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.frequency === 'one_time' && 'Competition runs once from start to end date'}
                    {formData.frequency === 'daily' && 'Progress resets at midnight daily. Runs within start-end period'}
                    {formData.frequency === 'weekly' && 'Progress resets every Monday. Runs within start-end period'}
                  </p>
                </div>

                {/* Staged Rewards UI */}
                {formData.winner_type === 'staged' && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <label className="block text-sm font-medium text-purple-800">
                        Reward Stages *
                      </label>
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          stages: [...formData.stages, { sales_target: '', target_value: '', reward_points: '' }]
                        })}
                        className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
                      >
                        + Add Stage
                      </button>
                    </div>
                    <div className="space-y-3">
                      {formData.stages.map((stage, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded border border-purple-200">
                          <span className="text-xs font-bold text-purple-600 w-8">#{idx + 1}</span>
                          <div className="flex-1">
                            <input
                              type="number"
                              value={formData.competition_type === 'revenue' ? stage.target_value : stage.sales_target}
                              onChange={(e) => {
                                const newStages = [...formData.stages];
                                if (formData.competition_type === 'revenue') {
                                  newStages[idx].target_value = e.target.value;
                                } else {
                                  newStages[idx].sales_target = e.target.value;
                                }
                                setFormData({...formData, stages: newStages});
                              }}
                              placeholder={formData.competition_type === 'revenue' ? 'Revenue ₹' : 'Sales #'}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>
                          <span className="text-xs text-slate-500">→</span>
                          <div className="w-24">
                            <input
                              type="number"
                              value={stage.reward_points}
                              onChange={(e) => {
                                const newStages = [...formData.stages];
                                newStages[idx].reward_points = e.target.value;
                                setFormData({...formData, stages: newStages});
                              }}
                              placeholder="Points"
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>
                          {formData.stages.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newStages = formData.stages.filter((_, i) => i !== idx);
                                setFormData({...formData, stages: newStages});
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-purple-600 mt-2">
                      Example: 12 sales → 200 pts, 16 sales → 500 pts. Highest milestone reached determines reward.
                    </p>
                  </div>
                )}

                {/* Prize Points - Conditional based on winner_type */}
                {(formData.winner_type === 'all_achievers' || formData.winner_type === 'first_to_target') && (
                  /* Target-based: Points per achiever */
                  <div className="bg-green-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-green-800 mb-1">
                      Points for {formData.winner_type === 'all_achievers' ? 'Each Achiever' : 'Winner'} *
                    </label>
                    <Input
                      type="number"
                      value={formData.prize_points_all_achievers}
                      onChange={(e) => setFormData({...formData, prize_points_all_achievers: e.target.value})}
                      placeholder="100"
                      className="bg-white"
                    />
                    <p className="text-xs text-green-600 mt-1">
                      {formData.winner_type === 'all_achievers' 
                        ? 'Each caller who reaches the target will earn these points'
                        : 'First person to reach the target wins these points'}
                    </p>
                  </div>
                )}

                {formData.winner_type === 'highest_at_end' && (
                  /* Ranking-based: 1st, 2nd, 3rd */
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-amber-800 mb-2">Prize Points (Ranking-based) *</label>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-amber-700 mb-1">🥇 1st Place *</label>
                        <Input
                          type="number"
                          value={formData.prize_points_1st}
                          onChange={(e) => setFormData({...formData, prize_points_1st: e.target.value})}
                          placeholder="500"
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-amber-700 mb-1">🥈 2nd Place</label>
                        <Input
                          type="number"
                          value={formData.prize_points_2nd}
                          onChange={(e) => setFormData({...formData, prize_points_2nd: e.target.value})}
                          placeholder="300"
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-amber-700 mb-1">🥉 3rd Place</label>
                        <Input
                          type="number"
                          value={formData.prize_points_3rd}
                          onChange={(e) => setFormData({...formData, prize_points_3rd: e.target.value})}
                          placeholder="100"
                          className="bg-white"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 mt-2">Points awarded to top 3 performers at competition end</p>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    />
                  </div>
                </div>

                {/* Available Points Warning */}
                {myKitty && formData.prize_points_1st && (
                  <div className={`p-3 rounded-lg ${
                    myKitty.available_balance >= parseInt(formData.prize_points_1st || 0)
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    <p className="text-sm">
                      Available Points: {myKitty.available_balance} | 
                      Required: {parseInt(formData.prize_points_1st || 0) + parseInt(formData.prize_points_2nd || 0) + parseInt(formData.prize_points_3rd || 0)}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreateCompetition}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="confirm-create-competition"
                >
                  Create Competition
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Competition Details Modal */}
      {showDetailsModal && selectedCompetition && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-semibold">{selectedCompetition.name}</h2>
                  {getStatusBadge(selectedCompetition.status)}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowDetailsModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Competition Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Target</p>
                  <p className="text-lg font-bold text-slate-900">
                    {selectedCompetition.winner_type === 'staged' && selectedCompetition.stages?.length > 0 ? (
                      <span className="text-base">
                        {selectedCompetition.stages.map((s, i) => (
                          <span key={i} className="inline-block mr-2">
                            {s.sales_target || s.target_value} → {s.reward_points}pts
                            {i < selectedCompetition.stages.length - 1 && ', '}
                          </span>
                        ))}
                      </span>
                    ) : selectedCompetition.competition_type === 'dual' ? (
                      <>
                        {selectedCompetition.target_value ? `₹${selectedCompetition.target_value.toLocaleString()}` : ''}
                        {selectedCompetition.target_value && selectedCompetition.sales_target ? ' OR ' : ''}
                        {selectedCompetition.sales_target ? `${selectedCompetition.sales_target} Sales` : ''}
                      </>
                    ) : selectedCompetition.competition_type === 'revenue' ? (
                      `₹${(selectedCompetition.target_value || 0).toLocaleString()}`
                    ) : (
                      `${selectedCompetition.sales_target || selectedCompetition.target_value || 0} Sales`
                    )}
                  </p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-600">Prize Pool</p>
                  <p className="text-lg font-bold text-amber-700">
                    {selectedCompetition.winner_type === 'all_achievers' || selectedCompetition.winner_type === 'first_to_target'
                      ? `${selectedCompetition.prize_points_all_achievers || selectedCompetition.prize_points_1st || 0} pts per achiever`
                      : <>
                          {selectedCompetition.prize_points_1st || 0}
                          {selectedCompetition.prize_points_2nd ? ` / ${selectedCompetition.prize_points_2nd}` : ''}
                          {selectedCompetition.prize_points_3rd ? ` / ${selectedCompetition.prize_points_3rd}` : ''} pts
                        </>
                    }
                  </p>
                </div>
              </div>

              {/* Leaderboard */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Leaderboard
                </h3>
                {selectedCompetition.leaderboard?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Rank</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Caller</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">
                            {selectedCompetition.competition_type === 'revenue' ? 'Revenue' : 'Sales'}
                          </th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCompetition.leaderboard.map((entry, idx) => (
                          <tr key={entry.caller_id} className={`border-t ${idx < 3 ? 'bg-amber-50' : ''}`}>
                            <td className="py-3 px-4">
                              {idx === 0 && <span className="text-2xl">🥇</span>}
                              {idx === 1 && <span className="text-2xl">🥈</span>}
                              {idx === 2 && <span className="text-2xl">🥉</span>}
                              {idx >= 3 && <span className="text-slate-500">#{entry.rank}</span>}
                            </td>
                            <td className="py-3 px-4 font-medium text-slate-900">{entry.caller_name}</td>
                            <td className="py-3 px-4 text-right">
                              {selectedCompetition.competition_type === 'revenue' ? '₹' : ''}
                              {entry.value.toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {entry.achieved ? (
                                <CheckCircle className="w-5 h-5 text-green-500 inline" />
                              ) : (
                                <Clock className="w-5 h-5 text-slate-400 inline" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No participants yet</p>
                )}
              </div>

              {/* Actions */}
              {selectedCompetition.status === 'active' && (user?.role === 'admin' || user?.role === 'manager') && (
                <div className="flex justify-end gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => handleEditCompetition(selectedCompetition)}
                    data-testid="edit-competition-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleCancelCompetition(selectedCompetition.id)}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Competition
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleCompleteCompetition(selectedCompetition.id)}
                    data-testid="complete-competition-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete & Award Points
                  </Button>
                </div>
              )}

              {/* Winners (for completed competitions) */}
              {selectedCompetition.status === 'completed' && selectedCompetition.winners?.length > 0 && (
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">🏆 Winners</h3>
                  <div className="space-y-3">
                    {selectedCompetition.winners.map(winner => (
                      <div key={winner.id || winner.caller_id} className="flex justify-between items-center p-2 bg-white rounded-lg shadow-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-green-700">
                            {winner.position ? `#${winner.position}` : '✓'} {winner.caller_name}
                          </span>
                          <span className="font-bold text-green-800">{winner.points_awarded} pts</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {winner.paid_in_cash ? (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                              ✓ Paid Cash ({winner.paid_cash_by_name})
                            </span>
                          ) : (user?.role === 'admin' || user?.role === 'manager') && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 border-amber-600 hover:bg-amber-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkPaidCash(winner.id);
                              }}
                              data-testid={`paid-cash-btn-${winner.id}`}
                            >
                              <DollarSign className="w-3 h-3 mr-1" /> Paid in Cash
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Competition Modal */}
      {showEditModal && selectedCompetition && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Edit Competition</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Competition Name</label>
                  <Input
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    data-testid="edit-competition-name"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                  />
                </div>

                {/* Non-editable fields shown for reference */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2">These fields cannot be changed after creation:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Type:</span>
                      <span className="ml-2 font-medium">{selectedCompetition.competition_type}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Target:</span>
                      <span className="ml-2 font-medium">
                        {selectedCompetition.competition_type === 'revenue' ? '₹' : ''}
                        {selectedCompetition.target_value}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Start:</span>
                      <span className="ml-2 font-medium">{selectedCompetition.start_date}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">End:</span>
                      <span className="ml-2 font-medium">{selectedCompetition.end_date}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button 
                  onClick={handleUpdateCompetition}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-competition-btn"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesCompetitions;
